import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { browserRefresh } from 'src/app/app.component';
import { LocalStorageService } from 'src/app/local-storage.service';
import { HelperService } from 'src/app/public/helper.service';
import { LocationService } from 'src/app/public/location.service';
import { WINDOW } from 'src/app/public/window-providers';
import { Category, ClientStore, LocationValid, OrderItem, StoreSaveMode } from 'src/app/stores/stores';
import { environment } from 'src/environments/environment';
import {
  AddOrderItem,
  AddOrderMeta,
  CheckExistingOrder,
  FetchSlots,
  LoadStore,
  RemoveOrderItem,
  SaveStoreByAlias,
  SetCurrentSelectedCategory,
  SetCurrentSelectedTopCategory,
  SlotSelected,
  UpdateLocation,
  UpdateOrderItem,
  ValidateStoreLocations
} from '../../+state/stores.actions';
import { CatalogList, SelectedStoreState } from '../../+state/stores.reducer';
import {
  getCurrentCartContent,
  getCurrentCartState,
  getCurrentCartStatus,
  getCurrentCartUuid,
  getCurrentOrderDeliveryMethod,
  getCustomerFooterHeight,
  getSelectedCategory,
  getSelectedLang,
  getSelectedStore,
  getSelectedStoreCatalog,
  getSelectedStoreStatus,
  getSelectedTopLevelCategory,
  getStoreLocations,
  getStoreOpenInDate,
  getStoreOpeningInfo,
  getUserLanguage,
} from '../../+state/stores.selectors';
import { CheckoutService } from '../../store-checkout/checkout.service';
import { Slot } from '../../types/AvailableSlotsResponse';
import { DELIVERY_METHOD_VALUES } from '../../types/DeliveryMethod';
import StoreUtils from '../../utils/StoreUtils';
import { Capacitor } from '@capacitor/core';
import { LanguageService } from 'src/app/shared/language.service';
import UrlUtils from 'src/app/stores/utils/url-utils';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Location } from '@angular/common';
import { InactivityService } from 'src/app/shared/inactivity.service';

@Component({
  selector: 'app-store-page',
  templateUrl: './store-page.component.html',
  styleUrls: ['./store-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorePageComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input() acceptTermVisible = false;

  @ViewChild('contentHeader') contentHeader: ElementRef;

  unsubscribe$: Subject<void> = new Subject<void>();
  selectedStore$: Observable<ClientStore>;
  selectedStore: ClientStore;
  availableSlots: Slot[];
  deliveryTime: Slot;
  userLang$: Observable<string>;
  browserTimeZone: string;
  categorySearch = '';
  searchSelectedCatList: number[] = [];
  menuCategories: Category[];
  topLevelCategories: Category[];
  selectedCat: number;
  selectedTopCat = -1;
  topLevelChanged = false;
  hasOtherTopLevelCategories: boolean;
  catalog: CatalogList;
  selectedCatalog: number;
  currentCartStatus: string;
  currentCartUuid: string;
  cartItems: OrderItem[] = [];
  locationData: LocationValid;
  showPayOnlineButton = false;
  standaloneUrl: string;
  selectedLang: string;
  fbCode: string;
  isAdminOrderCaptureUpdate: boolean;
  selectedStoreLocale: string;
  selectedStoreCurrency: string;
  selectedStoreCurrencySymbol: string;
  isOneClickToAddBasket = false;
  basketEnabled = false;
  selectedStoreStatus$: Observable<string>;
  deviceIdentifierAdded = false;
  topCatalogView = false;
  openInDate = true;

  navMaxLeftScroll = {};
  navMaxRightScroll = {};

  contentHeaderHeight = 120;
  tilesNavHeight = 0;
  safeAreaHeight: number = 0;
  skipScrollSpy = false;
  skipScrollTo = false;
  scrollOnLoad = true;
  skipHideSearchBar = false;

  isGoApp = false;
  customerFooterHeight: number;
  topCategoryContainerMarginBottom: number;

  isIOS: boolean;
  isIOSApp: boolean;
  catalogLayoutView: 'LIST' | 'TILES' = 'LIST';
  mdThreshold = 768;
  isCategoryAction = false;
  isProductDetailsAction = false;

  public cookieBarHeight: number = 0;

  expandCategory = new EventEmitter<any>();
  private scrollTimeout: any;
  private horizontalScrollTimeout: any;
  private lastScrollTop = 0;

  private wishDeliveryTime: string = null;
  scrollSpyActive: boolean;
  isLoading: boolean;

  public searchBarTopHidden: number = 0;
  private scrollDirection: 'UP' | 'DOWN';
  private previousScrollPosition: number;

  constructor(
    private store: Store<SelectedStoreState>,
    public storageService: LocalStorageService,
    private sanitizer: DomSanitizer,
    private cookieService: CookieService,
    private locationService: LocationService,
    private location: Location,
    public checkoutService: CheckoutService,
    @Inject(WINDOW) public window: Window,
    public router: Router,
    private helper: HelperService,
    private _ref: ChangeDetectorRef,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private inActivityService: InactivityService
  ) {}

  ngOnInit(): void {
    this.setLoadingValue(true);
    const urlLocationMatch = this.router.url.match(/\/#l\/([^/?#]+)/);
    const urlLocation = urlLocationMatch ? urlLocationMatch[1] : null;

    if (urlLocation) {
      this.store.dispatch(new AddOrderMeta('deliveryMethod', DELIVERY_METHOD_VALUES.IN_STORE_LOCATION, {
        src: 'StorePageComponent',
        description: 'Delivery method changed to IN_STORE_LOCATION'
      }));
    }

    if (this.helper.isIOS()) {
      SafeArea.getSafeAreaInsets().then(({ insets }) => {
        this.safeAreaHeight = insets.top;
      });

      SafeArea.addListener('safeAreaChanged', data => {
        const { insets } = data;
        this.safeAreaHeight = insets.top;
      });
    }

    this.isIOSApp = Capacitor.getPlatform() === 'ios';
    
    dayjs.extend(utc);
    dayjs.extend(timezone);
    this.selectedStore$ = this.store.pipe(select(getSelectedStore));
    this.userLang$ = this.store.select(getUserLanguage);
    this.selectedStoreStatus$ = this.store.pipe(select(getSelectedStoreStatus));
    this.browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.isGoApp = this.helper.isGoApp();

    if (this.storageService.getSavedState('categorySearch') && !browserRefresh) {
      this.categorySearch = this.storageService.getSavedState('categorySearch');
    }

    this.selectedStore$.pipe(takeUntil(this.unsubscribe$)).subscribe((selectedStore) => {
      this.selectedStore = selectedStore;

      if (this.selectedStore?.id && urlLocation) {
        this.store.dispatch(new ValidateStoreLocations(this.selectedStore.id, urlLocation));
      }

      if (!selectedStore) {
        const storeAlias = this.route.snapshot.params?.storeAlias;

        this.store.dispatch(new SaveStoreByAlias(storeAlias, `https://${storeAlias}.${environment.envDomain}`, StoreSaveMode.URL));
        this.store.dispatch(new LoadStore(storeAlias));
      }

      this.catalogLayoutView = selectedStore?.settings?.CATALOG_LAYOUT_VIEW === 'ENABLED' ? 'TILES' : 'LIST';

      this.isAdminOrderCaptureUpdate = this.locationService.isAdminOrderCaptureUpdate();
      if (this.selectedStore?.address && this.selectedStore?.currency) {
        this.selectedStoreLocale = StoreUtils.getStoreLocale(this.selectedStore);
        this.selectedStoreCurrency = StoreUtils.getCurrencyIsoCode(this.selectedStore);
        this.selectedStoreCurrencySymbol = StoreUtils.getCurrencySymbol(this.selectedStore);
      }
      if (this.selectedStore?.settings) {
        this.fbCode = this.selectedStore.settings.FACEBOOK_PIXEL_TRACKING_ID;
        this.isOneClickToAddBasket = this.selectedStore.settings.ADD_BASKET_ONE_CLICK;
        this.basketEnabled = this.selectedStore.settings.BASKET_ENABLED;
      }
    });

    this.store
      .select(getSelectedLang)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.selectedLang = value;
      });

      combineLatest([
        this.store.select(getStoreOpeningInfo),
        this.store.select(getCurrentOrderDeliveryMethod),
      ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([storeOpeningInfo, currentDeliveryMethod]) => {
      const slots = storeOpeningInfo.slots;
      if (slots.selectedSlot?.startTime) {
        this.deliveryTime = slots.selectedSlot;
        this.availableSlots = slots.availableSlots;
        // Needs to be done in the following order, first 'deliveryMethod' then 'wishTime'
        const defaultDeliveryMode = this.selectedStore?.settings?.DEFAULT_DELIVERY_MODE;
        if (defaultDeliveryMode && currentDeliveryMethod == null) {
          const deliveryMethod = DELIVERY_METHOD_VALUES[defaultDeliveryMode];
          if (deliveryMethod && this.selectedStore?.settings['DELIVERY_' + defaultDeliveryMode]) {
            this.store.dispatch(new AddOrderMeta('deliveryMethod', deliveryMethod, {
              src: 'StorePageComponent',
              description: 'Store opening info changed'
            }));
          } else {
            this.store.dispatch(new AddOrderMeta('deliveryMethod', null, {
              src: 'StorePageComponent',
              description: 'Store opening info changed'
            }));
          }
        }

        const wishDate = new Date(this.wishDeliveryTime);
        const deliveryDate = new Date(this.deliveryTime.startTime);

        wishDate.setMilliseconds(0);
        deliveryDate.setMilliseconds(0);

        if (wishDate.getTime() !== deliveryDate.getTime()) {
          this.wishDeliveryTime = deliveryDate.toISOString();

          this.store.dispatch(
            new AddOrderMeta('wishTime', this.wishDeliveryTime, {
              src: 'StorePageComponent',
              description: 'Store opening info changed',
            })
          );
        }
      } else {
        this.deliveryTime = {
          startTime: storeOpeningInfo.date,
          endTime: null,
          totalOrders: 0,
          isDisabled: false,
        };
        this.availableSlots = [];
      }
      this._ref.detectChanges();
    });
    this.store
      .pipe(
        takeUntil(this.unsubscribe$),
        select(getStoreOpenInDate),
        filter((openInDate) => openInDate != null)
      )
      .subscribe((openInDate) => {
        this.openInDate = openInDate;
      });

    combineLatest([
      this.store.select(getSelectedStoreCatalog),
      this.store.select(getSelectedCategory),
      this.store.select(getSelectedTopLevelCategory),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        ([catalog, category, selectedTopCat]) => {
          this.catalog = catalog;
          this.selectedCatalog = catalog.data.catalogId;
          this.topLevelCategories = catalog.data.topLevelCategories?.filter((top) =>
            catalog.data.categories?.some((c) => c.topLevelCategoryId === top.categoryId)
          );
          if (this.topLevelCategories?.length) {
            if (selectedTopCat < 0 || category < 0) {
              const currentCategory = catalog.data.categories?.find((c) => c.categoryId == category);
              this.selectedTopCat = currentCategory ? currentCategory.topLevelCategoryId : this.topLevelCategories[0].categoryId;

              this.store.dispatch(new SetCurrentSelectedTopCategory(this.selectedTopCat));
            } else {
              this.selectedTopCat = selectedTopCat;
            }

            this.menuCategories = catalog.data.categories?.filter((c) => c.topLevelCategoryId == this.selectedTopCat);

            if (!this.menuCategories?.some((c) => c.categoryId == this.selectedCat)) {
              this.selectedCat = this.menuCategories?.[0]?.categoryId ?? -1;
              this.topLevelChanged && this.updateUrlWithoutNavigating(this.selectedCat);
              this.topLevelChanged = false;
              this.store.dispatch(new SetCurrentSelectedCategory(this.selectedCat));
            }

            this.hasOtherTopLevelCategories = catalog.data.categories?.some((c) => !c.topLevelCategoryId);
          } else {
            this.menuCategories = catalog.data.categories;
          }

          if (!this.selectedCat || this.selectedCat < 0) {
            this.selectedCat = this.menuCategories?.[0]?.categoryId ?? -1;
          }

          if (category != null && this.selectedCat !== category) {
            this.selectedCat = category;
          }
          if (this.selectedCat > 0 && this.selectedCat !== category && this.selectedCat !== this.menuCategories[0]?.categoryId) {
            this.scrollToCategory(this.selectedCat);
          }

          this._ref.detectChanges();

          if (category > 0) {
            this.expandCategory.emit({ categoryId: category, toggle: 'off' });
            if (this.scrollOnLoad) {
              this.scrollOnLoad = false;
              setTimeout(() => this.scrollToCategory(this.selectedCat), 300);
            }
          }

          this.observeNavScroll('nav-tab-wrapper');
          this.observeNavScroll('top-level-nav-tab-wrapper');
        },
        (err) => console.log('Error:', err)
      );

    combineLatest([
      this.store.select(getCurrentCartStatus),
      this.store.select(getCurrentCartUuid),
      this.store.select(getCurrentCartContent),
      this.store.select(getStoreLocations),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        ([cartStatus, cartUuid, cartContent, storeLocations]) => {
          this.currentCartStatus = cartStatus;
          switch (cartStatus) {
            case 'LOADED':
              this.currentCartUuid = cartUuid;
              this.cartItems = cartContent;

              const hashParam = window.location.hash.split('/');
              if (hashParam[0] === '#l' && hashParam[1] && storeLocations?.storeLocationsState?.locationState) {
                this.locationData = storeLocations.storeLocationsState.locationState;
                this.locationService.setLocationUrlType(this.selectedStore.aliasName, 'url-location');
                this.locationService.setLocationOrder(this.selectedStore.aliasName, this.currentCartUuid, hashParam[1], this.locationData?.id,  storeLocations.storeLocationsState.locationState.locationType);
                this.locationService.removeLocationFromUrl(hashParam[1]);
                if (this.selectedStore.settings.ENABLE_STANDALONE_PAYMENT === 'ENABLE_OPEN_TAP' && (this.selectedStore.settings.OPEN_TAP || this.locationData?.openTab)) {
                  this.showPayOnlineButton = true;
                  this.standaloneUrl = `standalone-payment/l/${this.locationData?.id}`;
                }
              }
              
              if (storeLocations?.storeLocationsState?.locationState && this.currentCartUuid !== this.locationService.getLocationOrderUuid()) {
                this.locationService.setLocationOrder(this.selectedStore.aliasName, this.currentCartUuid, storeLocations.storeLocationsState.locationState.label, storeLocations.storeLocationsState.locationState.id,  storeLocations.storeLocationsState.locationState.locationType);
              }
              
              if (
                hashParam[0] !== '#l' &&
                this.locationService.getLocationOrder() &&
                this.currentCartUuid === this.locationService.getLocationOrder().split('_')[0] &&
                this.locationService.getLocationOrder().split('_')[2]
              ) {

                if (this.router.url.includes('#l/')) {
                  this.locationService.removeLocationFromUrl(this.locationData?.label);
                }

                if (this.selectedStore.settings.ENABLE_STANDALONE_PAYMENT === 'ENABLE_OPEN_TAP') {
                  this.showPayOnlineButton = true;
                  const locationId = this.locationService.getLocationOrder().split('_')[2];
                  this.standaloneUrl = `standalone-payment/l/${locationId}`;
                }
              }
              break;
            case 'ITEMADDED':
            case 'ITEMUPDATED':
            case 'ITEMREMOVED':
              this.store.dispatch(
                new CheckExistingOrder(this.selectedStore.id, this.currentCartUuid, 'CHECKEXISTING', this.selectedLang, null, {
                  src: 'StorePageComponent',
                  description: 'Cart status changed',
                })
              );
              break;
          }
          this.setLoadingValue(false);
          this._ref.detectChanges();
        },
        (err) => console.log('Error:', err)
      );

    combineLatest([this.store.select(getStoreLocations), this.store.select(getCurrentCartState), this.store.select(getSelectedStore)])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([location, order, store]) => {
        if (store?.id) {
          if (this.locationService.getLocationLabel() && order?.uuid && location) {
            if (location?.storeLocationsState?.locationState?.id && order.location !== location.storeLocationsState.locationState.label && this.locationService.getLocationType() == 'url-location') {
              this.store.dispatch(new UpdateLocation(location.storeLocationsState.locationState.id, this.helper.getDeviceID()));
            }
          } else if (order?.uuid && !this.deviceIdentifierAdded) {
            if (this.helper?.deviceIdentifierCheck?.flag === true && this.helper?.deviceIdentifierCheck?.orderId === order?.uuid) {
              this.deviceIdentifierAdded = true;
            } else {
              if (!this.isAdminOrderCaptureUpdate && this.locationService.getLocationType() == 'url-location') {
                this.store.dispatch(new UpdateLocation(null, this.helper.getDeviceID()));
              }
              this.helper.deviceIdentifierCheck.flag = true;
              this.helper.deviceIdentifierCheck.orderId = order.uuid;
            }
          }
        }
      });


    if (this.isGoApp) {
      this.store.select(getCustomerFooterHeight)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((customerFooterHeight) => {
          this.customerFooterHeight = customerFooterHeight;
          let buttonWrapperElement = document.getElementById('button-wrapper');
          this.renderer.setStyle(buttonWrapperElement, 'bottom', `${this.customerFooterHeight}px`);

        });
    }

    
    this.inActivityService.setModalShown(true);
    if (this.selectedStore.settings.INACTIVITY_OPTION) {
      this.inActivityService.setTimeout(this.selectedStore);
    }

    this.clearSearchCategory();
    this.getUrlLangRTL();
    this.checkUrl();

    if (this.helper.isAdminUI()) {
      this.getAppBody().addEventListener('scroll', this.onWindowScroll.bind(this));
    } else {
      window.addEventListener('scroll', this.onWindowScroll.bind(this));
    }
  }
  checkUrl() {
    const currentUrl = this.router.url;  // Get the current URL from Router
    this.isCategoryAction = UrlUtils.checkUrl(currentUrl);
  }

  ngAfterViewChecked(): void {
    let buttonWrapperElement = document.getElementById('button-wrapper');
    if (this.customerFooterHeight + (buttonWrapperElement?.offsetHeight ?? 0) !== this.topCategoryContainerMarginBottom) {
      this.topCategoryContainerMarginBottom = this.customerFooterHeight + (buttonWrapperElement?.offsetHeight ?? 0);
    }

    if (this.cookieBarHeight !== this.helper.getCookieBarHeight()) {
      this.cookieBarHeight = this.helper.getCookieBarHeight();
    }
    this.calculateTilesNavHeight();
    this._ref.detectChanges();
  }

  isMobileView(): boolean {
    return this.window.innerWidth <= 576;
  }

  decreaseItemFromCart(item: OrderItem) {
    if (item.quantity > 1) {
      let childItemRequests = [];
      if (item.childOrderItems) {
        childItemRequests = item.childOrderItems.map((oItem) => ({
          offerId: oItem.offerId,
          quantity: oItem.quantity,
        }));
      }
      item.quantity -= 1;
      if (item.variantOfferId) {
        childItemRequests.push({
          offerId: item.variantOfferId,
          quantity: item.quantity,
        });
      }
      this.store.dispatch(
        new UpdateOrderItem(this.selectedStore.id, this.currentCartUuid, item.uuid, {
          childItemRequests,
          comment: item.comment,
          offerId: item.offerId,
          quantity: item.quantity,
          wishTime: item.wishTime
        })
      );
    } else {
      item.quantity -= 1;
      this.store.dispatch(new RemoveOrderItem(this.selectedStore.id, this.currentCartUuid, item.uuid));
    }
  }

  ngAfterViewInit() {
    this.observeContentHeaderResize();
    this.appendOnScroll('nav-tab-wrapper');
    this.appendOnScroll('top-level-nav-tab-wrapper');
    this.initializeScrollSpy();
  }

  appendOnScroll(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.onscroll = () => this.observeNavScroll(elementId);
    }
  }

  observeContentHeaderResize() {
    const contentHeaderEl = this.contentHeader?.nativeElement;
    if (contentHeaderEl) {
      new ResizeObserver(() => {
        if (this.contentHeaderHeight !== contentHeaderEl.offsetHeight) {
          this.contentHeaderHeight = contentHeaderEl.offsetHeight;

        }

        this.calculateTilesNavHeight();

        this._ref.detectChanges();
        this.observeNavScroll('nav-tab-wrapper');
        this.observeNavScroll('top-level-nav-tab-wrapper');
      }).observe(this.contentHeader.nativeElement);
    }
  }

  observeNavScroll(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      this.navMaxLeftScroll[elementId] = element.scrollLeft === 0;
      this.navMaxRightScroll[elementId] = element.scrollLeft === element.scrollWidth - element.clientWidth;
    }
    this._ref.detectChanges();
  }

  initializeScrollSpy() {
    window.addEventListener('scroll', this.trackScrolling.bind(this));
  }

  trackScrolling() {
    if (!this.scrollSpyActive) {
      this.scrollSpyActive = true;
      requestAnimationFrame(() => {
        this.doScrollSpy();
        this.scrollSpyActive = false;
      });
    }
  }

  onWindowScroll() {
    this.doScrollSpy();
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.doScrollSpy();
    }, 300);
    const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;
    const scrollDirection: 'UP' | 'DOWN' = 
      currentScrollPosition > this.previousScrollPosition || 
      (!this.helper.isAdminUI() && this.skipHideSearchBar) ? 
      'DOWN' : 'UP';

    this.previousScrollPosition = currentScrollPosition;
    this.scrollDirection = scrollDirection;

    this.handleSearchBarVisibility(this.scrollDirection);
  }

  handleSearchBarVisibility(scrollDirection: 'UP' | 'DOWN') {
    if (this.isMobileView()) {
      if (scrollDirection == 'DOWN') {

        this.searchBarTopHidden = -(this.getSearchBarHeight() + this.getCategoryImageViewHeight());
      }

      if (scrollDirection == 'UP') {
        this.searchBarTopHidden = 0;
      }

    } else {
      this.searchBarTopHidden = 0;
    }
  }

  getCategoryImageViewHeight() : number {
    let categoryImageViewHeight: number = 0;

    if (this.isMobileView()) {
      let topLevelNavHeight = document.getElementById('top-level-nav-tab-wrapper')?.offsetHeight||0;

      let contentHeaderPaddingBottom = this.catalogLayoutView == 'TILES' ? 30 : 0
      categoryImageViewHeight = contentHeaderPaddingBottom + topLevelNavHeight;
    }

    return categoryImageViewHeight;
  }

  getSearchBarHeight() : number {
    let searchBarHeight: number = 0;
    let siblingSelectorHeight: number = 0;

    if (this.isMobileView()) {
      searchBarHeight = document.getElementById('search-bar')?.offsetHeight||0;
      siblingSelectorHeight = document.getElementById('sibling-selector')?.offsetHeight||0;
    }

    return searchBarHeight + siblingSelectorHeight;
  }

  doScrollSpy() {
    if (!this.skipScrollSpy) {
      const sections: NodeListOf<HTMLElement> = document.querySelectorAll('.category-tab');

      if (sections.length === 0) {
        return;
      }

      const scrollPos = document.documentElement.scrollTop;
      let id;
      if(scrollPos === 0) {
        id = sections[0].id;
      }
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollPos - this.offset(); // Section top relative to document
        const sectionBottom = sectionTop + rect.height;

        // Check if scroll position is within the section's boundaries
        if (scrollPos >= sectionTop && scrollPos < sectionBottom) {
          id = section.id;
          break; // Exit loop when the correct section is found
        }
      }

      if (id) {
        const categoryId = Number(id.replace(/\D/g, ''));
        if (this.selectedCat !== categoryId) {
          this.scrollNavTo(categoryId); // Scrolls the navigation
          this.selectCategory(categoryId); // Updates the state with new category
        }
      }
    }
  }

  scrollToCategory(categoryId: number) {
    if (!this.skipScrollTo) {
      const bodyRect = document.body.getBoundingClientRect().top;
      const element = document.getElementById(`category${categoryId}`);
      const elementRect = element?.getBoundingClientRect()?.top || 0;
      const elementPosition = elementRect - bodyRect;
      let top = elementPosition - this.offset();

      this.doSkipScrollSpy(1000); // Temporarily disables scroll spy to avoid screen flickering

      if (!this.helper.isAdminUI()) {
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        const appBody = this.getAppBody();
        top = top - this.getCategoryImageViewHeight() -  this.helper.getAdminBreadcrumbNavigationHeight() - this.safeAreaHeight - (this.isMobileView() ? this.helper.getAdminHeaderHeight() : 0);
        appBody.scrollBy({ top, behavior: 'smooth' });
      }
    }
  }

  getAppBody() {
    return document.getElementsByClassName('app-body admin')[0];
  }

  offset(): number {
    return this.contentHeaderHeight + this.helper.getAdminHeaderHeight() + this.tilesNavHeight - (this.getCategoryImageViewHeight() + this.getSearchBarHeight());
  }

  scroolToTop() {
    const element = document.getElementById('top-anchor');
    if (element) {
      const rect = element.getBoundingClientRect();
      const offsetTop = window.scrollY + rect.top - (this.helper.getAdminBreadcrumbNavigationHeight()); // get offset from viewport top
      window.scrollTo({top: offsetTop,behavior: 'auto'});// 'auto' for immediate scroll
    }
  }
  
  timeoutScrollSpy: any;
  doSkipScrollSpy(time = 2000) {
    this.skipScrollSpy = true;
    clearTimeout(this.timeoutScrollSpy);
    this.timeoutScrollSpy = setTimeout(() => (this.skipScrollSpy = false), time);
  }

  timeoutHideSearchBar: any;
  doSkipHideSearchBar(time = 1000) {
    this.skipHideSearchBar = true;
    clearTimeout(this.timeoutHideSearchBar);
    this.timeoutHideSearchBar = setTimeout(() => (this.skipHideSearchBar = false), time);
  }

  timeoutScrollTo: any;
  doSkipScrollTo(time = 1500) {
    this.skipScrollTo = true;
    clearTimeout(this.timeoutScrollTo);
    this.timeoutScrollTo = setTimeout(() => (this.skipScrollTo = false), time);
  }

  scrollNavToLeft(elementId: string) {
    const container = document.getElementById(elementId);
    const current = container.scrollLeft;
    const offset = container.clientWidth * 0.7;
    container.scrollTo({ left: current - offset, behavior: 'smooth' });
  }

  scrollNavToRight(elementId: string) {
    const container = document.getElementById(elementId);
    const current = container.scrollLeft;
    const offset = container.clientWidth * 0.7;
    container.scrollTo({ left: current + offset, behavior: 'smooth' });
  }

  scrollNavTo(categoryId: number) {
    const scrollSpeed = this.getScrollSpeed();
    if (this.selectedCat !== categoryId) {
      const behavior = this.helper.isIOS() ? 'smooth' : 'auto';
      const tabElement = document.getElementById(`tab${categoryId}`);
      if (tabElement) {
        clearTimeout(this.horizontalScrollTimeout);
        const dynamicDelay = scrollSpeed > 50 ? 150 : 300;

        this.horizontalScrollTimeout = setTimeout(() => {
          tabElement.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
        }, dynamicDelay);
      }
    }
  }
// Helper function to calculate scroll speed
getScrollSpeed() {
  const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollSpeed = Math.abs(currentScrollTop - this.lastScrollTop); // Speed is the distance scrolled since the last call
  this.lastScrollTop = currentScrollTop;
  return scrollSpeed;
}
  shouldDisplaySiblingSelection() {
    return this.selectedStore?.relation?.siblingStores?.some((sibling) => !sibling.isIndependent);
  }

  shouldDisplayDateSelection(): boolean {
    return (this.selectedStore?.settings?.DELIVERY_REQUEST_ORDER_DATE_UPFRONT && this.selectedStore?.settings?.DEFAULT_DELIVERY_MODE) ||
      this.selectedStore?.settings?.WISH_DATE_PER_ORDER_ITEM === 'ENABLED';
  }

  shouldDisplaySlotSelection(): boolean {
    return !StoreUtils.isAsapOrderEnabled(this.selectedStore, this.selectedStore?.settings?.DEFAULT_DELIVERY_MODE);
  }

  onDateChanged(date: Slot) {
    this.deliveryTime = date;
    if (
      this.selectedStore.settings.DEFAULT_DELIVERY_MODE &&
      this.selectedStore.settings['DELIVERY_' + this.selectedStore.settings.DEFAULT_DELIVERY_MODE]
    ) {
      this.store.dispatch(new AddOrderMeta('deliveryMethod', DELIVERY_METHOD_VALUES[this.selectedStore.settings.DEFAULT_DELIVERY_MODE], {
        src: 'StorePageComponent',
        description: 'Date changed'
      }));
    } else {
      this.store.dispatch(new AddOrderMeta('deliveryMethod', null, {
        src: 'StorePageComponent',
        description: 'Date changed'
      }));
    }
    this.store.dispatch(
      new FetchSlots(
        this.selectedStore.id,
        this.selectedStore.settings.DEFAULT_DELIVERY_MODE || 'NO_LOCATION',
        this.deliveryTime.startTime,
        {
          src: 'StorePageComponent',
          description: 'Date changed',
        }
      )
    );
  }

  onSelectedSlotChanged(selectedSlot: Slot) {
    this.deliveryTime = selectedSlot;
    if (
      !this.locationService.getLocationType() &&
      this.selectedStore.settings.DEFAULT_DELIVERY_MODE &&
      this.selectedStore.settings['DELIVERY_' + this.selectedStore.settings.DEFAULT_DELIVERY_MODE]
    ) {
      this.store.dispatch(new AddOrderMeta('deliveryMethod', DELIVERY_METHOD_VALUES[this.selectedStore.settings.DEFAULT_DELIVERY_MODE], {
        src: 'StorePageComponent',
        description: 'Slot changed'
      }));
    } else if (!this.locationService.getLocationType()) {
      this.store.dispatch(new AddOrderMeta('deliveryMethod', null, {
        src: 'StorePageComponent',
        description: 'Slot changed'
      }));
    }
    if (!this.deliveryTime?.startTime) {
      return;
    }
    this.store.dispatch(
      new AddOrderMeta('wishTime', dayjs(this.deliveryTime.startTime).toISOString(), {
        src: 'StorePageComponent',
        description: 'Slot changed',
      })
    );
    this.store.dispatch(new SlotSelected(selectedSlot));
  }

  isTimeShowDisabled() {
    return this.checkoutService.isTimeShowDisabled();
  }

  onKeypressSearchCategory(event: any) {
    this.categorySearch = event.target.value.trim();
    this.storageService.setSavedState(this.categorySearch, 'categorySearch');
    if (this.categorySearch.length === 0) {
      this.searchSelectedCatList = [];
    }
  }

  clearSearchCategory() {
    this.categorySearch = '';
    this.searchSelectedCatList = [];
    this.storageService.removeSavedState('categorySearch');
  }

  onSelectCategory(categoryId: number, toggle = 'off') {
    this.expandCategory.emit({ categoryId, toggle });
    if (toggle === 'toggle') {
      this.doSkipScrollTo();
    }
    this.doSkipHideSearchBar();
    this.scrollToCategory(categoryId);
    this.selectCategory(categoryId);
  }

  onSelectTopLevelCategory(categoryId: number, toggle = 'off') {
    this.topCatalogView = false;
    this.selectTopLevelCategory(categoryId);
    setTimeout(() => this.calculateTilesNavHeight(), 100);
  }

  getUrlLangRTL() {
    const urlParams = new URLSearchParams(window.location.search);
    const ulang = urlParams.get('ulang');
    const clang = urlParams.get('clang');
    let rtlArray = ['fa', 'ar', 'iw', 'ku', 'ur'];
    if (ulang == clang && rtlArray.indexOf(ulang) != -1) {
      this.languageService.setLanguage(ulang);
    }
  }

  calculateTilesNavHeight() {
    if (window.innerWidth < this.mdThreshold && this.catalogLayoutView === 'TILES') {
      if ( document.getElementById('category-nav')?.offsetHeight + 2 || 0 !== this.tilesNavHeight) {
        this.tilesNavHeight = document.getElementById('category-nav')?.offsetHeight + 2 || 0;
      }
    } else {
      this.tilesNavHeight = 0;
    }
  }

  onSelectTopCatalogView() {
    this.topCatalogView = true;
  }

  selectCategory(categoryId: number) {
    if (this.selectedCat !== categoryId) {
      this.selectedCat = categoryId;
      if (!this.isProductDetailsAction) {
        this.updateUrlWithoutNavigating(categoryId);
      }
      this.store.dispatch(new SetCurrentSelectedCategory(categoryId));
    }
  }

  updateUrlWithoutNavigating(categoryId: string | number) {
    const newUrl = this.locationService.base_url(`category/${categoryId}`);
    this.location.replaceState(newUrl);
  }

  selectTopLevelCategory(categoryId: number) {
    if (this.selectedTopCat !== categoryId) {
      this.scroolToTop();
      this.topLevelChanged = true;
      this.store.dispatch(new SetCurrentSelectedTopCategory(categoryId));
    }
  }

  getBackgroundImage(url: any) {
    if (url) {
      return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
    }
    return '';
  }

  getSubdomain(): string {
    return environment.name === 'local' ? environment.envDomain + ':' + window.location.port : environment.envDomain;
  }

  viewProductDetails(event) {
    const { offerId, categoryId, inStock, isExpandable, isOrderable, basketEnabled } = event;
    if (!inStock || (!isExpandable && (!isOrderable || (!basketEnabled && !this.isAdminOrderCaptureUpdate)))) {
      return;
    }
    this.isProductDetailsAction = true;
    this.store.dispatch(new SetCurrentSelectedCategory(categoryId));
    this.router.navigateByUrl(this.locationService.base_url(`category/${categoryId}`)).then(() => {
      this.router.navigateByUrl(this.locationService.base_url(`offer/${offerId}`));
    });
  }

  isAllergenSelected(attributes, name) {
    const attr = attributes.find((item) => item.key === name);
    return attr ? attr.value : false;
  }

  onOneClickAddToCart(event) {
    const { offerId, categoryId, inStock, isDirectlyOrderable, isExpandable, isOrderable, basketEnabled } = event;
    this.doSkipScrollTo();
    if (
      !this.selectedStore.settings.ADD_BASKET_ONE_CLICK ||
      !isDirectlyOrderable ||
      !isOrderable ||
      !basketEnabled ||
      !inStock ||
      this.checkoutService.isClosedForOrdering()
    ) {
      this.viewProductDetails(event);
      return;
    }
    const wishTime = this.selectedStore.settings.WISH_DATE_PER_ORDER_ITEM === 'ENABLED' ? this.deliveryTime.startTime : null;
    if (wishTime && !this.openInDate) {
      alert('The store is closed at the selected time');
      return;
    }

    this.store.dispatch(
      new AddOrderItem(this.selectedStore.id, this.currentCartUuid, {
        offerId,
        quantity: 1,
        comment: '',
        childItemRequests: [],
        wishTime
      })
    );
  }

  cartItemsTotalValue() {
    if (this.cartItems?.length > 0) {
      return this.cartItems?.reduce(
        (total, item) => total + (item.discountType ? item.totalDiscountedPrice : item.totalNonDiscountedPrice),
        0
      );
    } else {
      return 0;
    }
  }

  payOnline() {
    if (this.standaloneUrl) {
      if (this.isAdminOrderCaptureUpdate) {
        const storeAlias = this.route.snapshot.params?.storeAlias;
        const url = `${window.location.protocol}//${storeAlias}.${this.getSubdomain()}/${this.standaloneUrl}`;
        this.window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(this.locationService.public_url() + this.standaloneUrl);
      }
    }
  }
  @HostListener('window:mousemove')
  refreshUserState() {
    if ( this.selectedStore.settings.INACTIVITY_OPTION && this.inActivityService.getModalShown() && this.cartItems?.length>0){
      this.inActivityService.refreshUserState(this.selectedStore);
    }
  }

  onViewOrder(event) {
    event.preventDefault();
    this.store.dispatch(new SetCurrentSelectedCategory(this.selectedCat));
    this.checkoutService.proceedToCheckout();
  }

  viewOrderItemDetails(item: OrderItem) {
    if (item.hierarchyLevel !== 'PARENT' && item.hierarchyLevel !== "GIFT_CARD") {
      return;
    }
    this.router.navigateByUrl(this.locationService.base_url(`orderItem/${item.uuid}`));
  }

  onDecreaseItemQty(item: OrderItem) {
    this.doSkipScrollTo();
    if (this.currentCartStatus !== 'LOADED') {
      return;
    }
    this.decreaseItemFromCart(item);
  }

  onIncreaseItemQty(item: OrderItem) {
    this.doSkipScrollTo();
    let childItemRequests = [];
    if (this.currentCartStatus !== 'LOADED') {
      return;
    }
    if (item.childOrderItems) {
      childItemRequests = item.childOrderItems.map((oItem) => ({
        offerId: oItem.offerId,
        quantity: oItem.quantity,
      }));
    }
    item.quantity += 1;
    if (item.variantOfferId) {
      childItemRequests.push({
        offerId: item.variantOfferId,
        quantity: item.quantity,
      });
    }
    this.store.dispatch(
      new UpdateOrderItem(this.selectedStore.id, this.currentCartUuid, item.uuid, {
        childItemRequests,
        comment: item.comment,
        offerId: item.offerId,
        quantity: item.quantity,
        wishTime: item.wishTime
      })
    );
  }
  setLoadingValue(event:boolean){
    this.isLoading = event;
  }
  showStoreClosedMessage(): boolean {
    return this.checkoutService.isClosedForOrdering() || !this.openInDate;
  }

  ngOnDestroy() {
    if (this.helper.isAdminUI()) {
      this.getAppBody().removeEventListener('scroll', this.onWindowScroll.bind(this));
    } else {
      window.removeEventListener('scroll', this.onWindowScroll.bind(this));
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.inActivityService.setModalShown(false);
  }

  getTotalQuantity() {
    return this.cartItems?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  }

  centerHeader(): boolean {
    const basketEnabled = this.locationService.isBasketEnabled() == null ? 
        this.selectedStore?.settings?.BASKET_ENABLED : 
        this.locationService.isBasketEnabled();

    return this.catalogLayoutView !== 'TILES' 
              && !this.isAdminOrderCaptureUpdate
              && !basketEnabled;
  }
}
